import axios from 'axios';
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { PencilSimple, Play, CheckCircle, Pause, ArrowClockwise, Prohibit, Package, Gear, Wrench, Trash, TrendDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg, canReleaseWo, hasCap } from '@/auth/AuthContext';
import { mfgApi, WO_STATUS_BADGE, TYPE_BADGE } from './mfgApi';
import AttachmentsPanel from './AttachmentsPanel';
import LinkedDocsPanel from '@/pages/docs/LinkedDocsPanel';
import CompleteCapture from './CompleteCapture';

export default function WorkOrderDetailDialog({ workOrderId, onClose, onChanged, onEdit }) {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const canDeleteMfg = hasCap(user, 'mfg_delete');
  const [wo, setWo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [completeOpen, setCompleteOpen] = useState(false);
  const [replenish, setReplenish] = useState(null);
  // 2026-11-05 · 現場の記録・品質チェック（読み取り専用）
  const [qc, setQc] = useState(null);
  // 2026-11-05 · この手配番号で出した分（数量と概算金額）
  const [tehaiIssues, setTehaiIssues] = useState(null);

  const load = useCallback(() => {
    if (!workOrderId) return;
    mfgApi.workOrder(workOrderId).then((w) => {
      setWo(w);
      const no = w?.tehai_no || w?.manufacturing_no;
      if (no) {
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/mfg/tehai/issues`,
          { params: { tehai_no: no } })
          .then((r) => setTehaiIssues(r.data)).catch(() => setTehaiIssues(null));
      } else setTehaiIssues(null);
    }).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/mfg/work-orders/${workOrderId}/qc`)
      .then((r) => setQc(r.data)).catch(() => setQc(null));
  }, [workOrderId]);
  useEffect(() => { load(); setHoldOpen(false); setHoldReason(''); setCompleteOpen(false); setReplenish(null); }, [load]);

  if (!workOrderId) return null;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const res = await fn(); if (okMsg) toast.success(okMsg(res)); load(); onChanged?.(); return res; }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setBusy(false); }
  };

  const release = async () => {
    setBusy(true);
    try {
      const pv = await mfgApi.releasePreview(workOrderId);
      if (pv?.auto_replenish && (pv.suggestions || []).length > 0) {
        const sel = {};
        pv.suggestions.forEach((s) => { sel[s.component_item_id] = { on: true, qty: s.recommended_qty }; });
        setBusy(false);
        setReplenish({ suggestions: pv.suggestions, sel });
        return;
      }
    } catch { /* preview failed — release plainly */ }
    setBusy(false);
    await doRelease([]);
  };
  const doRelease = (replenishments) => {
    setReplenish(null);
    return run(() => mfgApi.releaseWorkOrder(workOrderId, { replenishments }),
      (r) => (r?.created_requests ? `${r.created_requests} ${t('mfg.wo.createdReqs')}` : t('mfg.common.saved')));
  };
  const confirmReplenish = () => {
    const chosen = replenish.suggestions
      .filter((s) => replenish.sel[s.component_item_id]?.on && Number(replenish.sel[s.component_item_id]?.qty) > 0)
      .map((s) => ({ component_item_id: s.component_item_id, qty: Number(replenish.sel[s.component_item_id].qty) }));
    doRelease(chosen);
  };
  const setSel = (id, patch) => setReplenish((p) => ({ ...p, sel: { ...p.sel, [id]: { ...p.sel[id], ...patch } } }));
  const start = () => run(() => mfgApi.startWorkOrder(workOrderId), () => t('mfg.common.saved'));
  const resume = () => run(() => mfgApi.resumeWorkOrder(workOrderId), () => t('mfg.common.saved'));
  const submitComplete = (payload) => { run(() => mfgApi.completeWorkOrder(workOrderId, payload), () => t('mfg.common.saved')); setCompleteOpen(false); };
  const cancel = () => { if (!window.confirm(t('mfg.wo.confirmCancel'))) return; run(() => mfgApi.cancelWorkOrder(workOrderId), () => t('mfg.common.saved')); };
  const doHold = () => { run(() => mfgApi.holdWorkOrder(workOrderId, { reason: holdReason || null }), () => t('mfg.common.saved')); setHoldOpen(false); setHoldReason(''); };
  const del = async () => {
    if (!window.confirm(t('mfg.wo.deleteConfirm') + (wo?.wo_no ? ` (${wo.wo_no})` : ''))) return;
    setBusy(true);
    try {
      await mfgApi.deleteWorkOrder(workOrderId);
      toast.success(t('mfg.wo.deleted'));
      onChanged?.();
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setBusy(false); }
  };

  const info = (label, val) => (<div><p className="text-[11px] text-zinc-400">{label}</p><p className="text-zinc-800">{val || '—'}</p></div>);
  const status = wo?.status;
  const comps = wo?.components || [];
  const reqs = wo?.shortage_requests || [];

  return (
    <Dialog open={!!workOrderId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="wo-detail-dialog">
        {!wo ? (
          <><DialogHeader><DialogTitle className="sr-only">{t('mfg.wo.detail')}</DialogTitle></DialogHeader><div className="py-16 text-center text-sm text-zinc-400">…</div></>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-sm text-zinc-500">{wo.wo_no}</span>
                <span><span data-no-translate>{wo.product_name}</span></span>
                <span className="text-sm font-normal text-zinc-500">× {wo.qty} {wo.product_uom || ''}</span>
                <Badge variant="outline" className={WO_STATUS_BADGE[wo.status]} data-testid="wo-detail-status">{wo.status_label}</Badge>
                {wo.overdue && <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{t('mfg.wo.overdue')}</Badge>}
                {wo.has_shortage && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">{t('mfg.wo.shortage')}</Badge>}
                {wo.mkt_order_id && (
                  <Badge variant="outline" onClick={() => window.open(`/marketing/orders/${wo.mkt_order_id}`, '_blank')}
                    className="bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 cursor-pointer hover:bg-fuchsia-100" data-testid="wo-detail-mkt-link">
                    営業受注 ↗
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {info(t('mfg.wo.dueDate'), wo.due_date)}
              {info(t('mfg.wo.mfgNo'), wo.manufacturing_no)}
              {info(t('mfg.wo.equipment'), wo.equipment_name)}
              {info(t('mfg.wo.location'), wo.location_id ? (wo.location_name || wo.location_code || '（場所名未設定）') : '完成品')}
              {info(t('mfg.wo.createdBy'), wo.created_by)}
            </div>
            {wo.notes && <p className="text-sm text-zinc-600">{wo.notes}</p>}
            {status === 'on_hold' && wo.held_reason && <p className="text-sm text-rose-600"><b>{t('mfg.wo.heldReason')}:</b> {wo.held_reason}</p>}

            {/* actions */}
            {canWrite && status !== 'completed' && (
              <div className="flex flex-wrap items-center gap-2 sticky top-0 bg-white py-2 -mt-1 z-10 border-b border-zinc-200">
                {status === 'planned' && <>
                  {canReleaseWo(user) && <Button size="sm" data-testid="wo-release-btn" onClick={release} disabled={busy} className="h-8 bg-teal-700 hover:bg-teal-800"><Play size={14} className="mr-1" weight="fill" /> {t('mfg.wo.release')}</Button>}
                  <Button size="sm" variant="outline" data-testid="wo-edit-btn" onClick={() => onEdit?.(wo)} className="h-8"><PencilSimple size={14} className="mr-1" /> {t('mfg.common.edit')}</Button>
                </>}
                {(status === 'released' || status === 'on_hold') && (
                  <Button size="sm" data-testid="wo-start-btn" onClick={start} disabled={busy} className="h-8 bg-amber-500 hover:bg-amber-600"><Gear size={14} className="mr-1" /> {t('mfg.wo.start')}</Button>
                )}
                {status === 'on_hold' && (
                  <Button size="sm" variant="outline" data-testid="wo-resume-btn" onClick={resume} disabled={busy} className="h-8"><ArrowClockwise size={14} className="mr-1" /> {t('mfg.wo.resume')}</Button>
                )}
                {(status === 'released' || status === 'in_progress') && (
                  <Button size="sm" variant="outline" data-testid="wo-hold-btn" onClick={() => setHoldOpen((v) => !v)} disabled={busy} className="h-8"><Pause size={14} className="mr-1" /> {t('mfg.wo.hold')}</Button>
                )}
                {(status === 'released' || status === 'in_progress' || status === 'on_hold') && (
                  <Button size="sm" data-testid="wo-complete-btn" onClick={() => setCompleteOpen((v) => !v)} disabled={busy} className="h-8 bg-teal-700 hover:bg-teal-800"><CheckCircle size={14} className="mr-1" weight="fill" /> {t('mfg.wo.complete')}</Button>
                )}
                {status !== 'cancelled' && (
                  <Button size="sm" variant="ghost" data-testid="wo-cancel-btn" onClick={cancel} disabled={busy} className="h-8 text-rose-600 ml-auto"><Prohibit size={14} className="mr-1" /> {t('mfg.wo.cancel')}</Button>
                )}
                {canDeleteMfg && (status === 'planned' || status === 'cancelled') && (
                  <Button size="sm" variant="outline" data-testid="wo-delete-btn" onClick={del} disabled={busy} className={`h-8 text-rose-600 border-rose-200 hover:bg-rose-50 ${status === 'cancelled' ? 'ml-auto' : ''}`}><Trash size={14} className="mr-1" /> {t('mfg.wo.delete')}</Button>
                )}
              </div>
            )}

            {holdOpen && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2" data-testid="wo-hold-panel">
                <Input value={holdReason} onChange={(e) => setHoldReason(e.target.value)} placeholder={t('mfg.wo.holdPrompt')} className="h-9 flex-1 bg-white" data-testid="wo-hold-reason-input" />
                <Button size="sm" onClick={doHold} disabled={busy} className="h-9 bg-amber-500 hover:bg-amber-600" data-testid="wo-hold-confirm">{t('mfg.wo.hold')}</Button>
              </div>
            )}

            {replenish && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2.5" data-testid="wo-replenish-panel">
                <div className="flex items-start gap-2">
                  <TrendDown size={18} weight="duotone" className="text-indigo-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-indigo-900">{t('mfg.wo.replenishTitle')}</p>
                    <p className="text-xs text-indigo-700/80 mt-0.5">{t('mfg.wo.replenishHint')}</p>
                  </div>
                </div>
                <div className="rounded-md border border-indigo-100 bg-white overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-indigo-50/60 text-indigo-400 border-b border-indigo-100">
                      <th className="text-left px-2.5 py-1.5 font-medium">{t('mfg.bom.component')}</th>
                      <th className="text-right px-2.5 py-1.5 font-medium">{t('mfg.wo.onHand')}</th>
                      <th className="text-right px-2.5 py-1.5 font-medium">{t('mfg.wo.projected')}</th>
                      <th className="text-right px-2.5 py-1.5 font-medium">{t('mfg.wo.reorderPoint')}</th>
                      <th className="text-right px-2.5 py-1.5 font-medium">{t('mfg.wo.replenishQty')}</th>
                    </tr></thead>
                    <tbody>
                      {replenish.suggestions.map((s, i) => {
                        const sel = replenish.sel[s.component_item_id] || {};
                        return (
                          <tr key={s.component_item_id} className={`border-b border-zinc-50 ${sel.on ? '' : 'opacity-45'}`} data-testid={`wo-replenish-row-${i}`}>
                            <td className="px-2.5 py-1.5">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={!!sel.on} onChange={(e) => setSel(s.component_item_id, { on: e.target.checked })}
                                  className="h-3.5 w-3.5 accent-indigo-600" data-testid={`wo-replenish-check-${i}`} />
                                <span className="text-zinc-800">{s.name}{s.sku && <span className="ml-1 font-mono text-[10px] text-zinc-400">{s.sku}</span>}</span>
                              </label>
                            </td>
                            <td className="px-2.5 py-1.5 text-right tabular-nums text-zinc-600">{s.on_hand}</td>
                            <td className="px-2.5 py-1.5 text-right tabular-nums"><span className="text-rose-600 font-semibold">{s.projected}</span></td>
                            <td className="px-2.5 py-1.5 text-right tabular-nums text-zinc-600">{s.reorder_point}</td>
                            <td className="px-2.5 py-1.5 text-right">
                              <Input type="number" min="0" step="any" value={sel.qty ?? ''} disabled={!sel.on}
                                onChange={(e) => setSel(s.component_item_id, { qty: e.target.value })}
                                className="h-7 w-20 ml-auto text-right text-xs bg-white" data-testid={`wo-replenish-qty-${i}`} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setReplenish(null)} disabled={busy} data-testid="wo-replenish-cancel">{t('mfg.common.cancel')}</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => doRelease([])} disabled={busy} data-testid="wo-replenish-skip">{t('mfg.wo.replenishSkip')}</Button>
                  <Button size="sm" className="h-8 bg-teal-700 hover:bg-teal-800" onClick={confirmReplenish}
                    disabled={busy || !replenish.suggestions.some((s) => replenish.sel[s.component_item_id]?.on)}
                    data-testid="wo-replenish-confirm"><Play size={14} className="mr-1" weight="fill" /> {t('mfg.wo.replenishConfirm')}</Button>
                </div>
              </div>
            )}

            {completeOpen && (
              <CompleteCapture plannedQty={wo.qty} uom={wo.product_uom} busy={busy}
                onSubmit={submitComplete} onCancel={() => setCompleteOpen(false)} variant="panel" />
            )}

            {/* OEE (completed WOs) */}
            {status === 'completed' && wo.oee && (
              <section className="rounded-lg border border-zinc-200 overflow-hidden" data-testid="wo-oee-panel">
                <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50/60"><h3 className="text-sm font-semibold text-zinc-700">{t('mfg.wo.oeeResult')}</h3></div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-100">
                  {[
                    ['mfg.analytics.oee', wo.oee.oee, 'text-indigo-600'],
                    ['mfg.analytics.availability', wo.oee.availability, 'text-blue-600'],
                    ['mfg.analytics.performance', wo.oee.performance, 'text-amber-600'],
                    ['mfg.analytics.quality', wo.oee.quality, 'text-emerald-600'],
                  ].map(([k, v, c]) => (
                    <div key={k} className="bg-white p-3 text-center">
                      <p className="text-[11px] text-zinc-400">{t(k)}</p>
                      <p className={`text-xl font-bold tabular-nums ${c}`}>{v == null ? '—' : `${Math.round(v * 100)}%`}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 px-3 py-2 text-xs text-zinc-500 border-t border-zinc-200">
                  <span>{t('mfg.wo.goodQty')}: <b className="text-emerald-600">{wo.oee.good_qty}</b></span>
                  <span>{t('mfg.wo.scrapQty')}: <b className="text-rose-600">{wo.oee.scrap_qty}</b></span>
                  {wo.scrap_reason && <span>{t(`mfg.scrapReason.${wo.scrap_reason}`)}</span>}
                </div>
              </section>
            )}

            {/* Labor hours (実績 vs 標準) — completed WOs */}
            {status === 'completed' && (
              <section className="rounded-lg border border-zinc-200 overflow-hidden" data-testid="wo-labor-panel">
                <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50/60"><h3 className="text-sm font-semibold text-zinc-700">{t('mfg.wo.laborResult')}</h3></div>
                <div className="flex flex-wrap items-center gap-5 px-3 py-3 text-sm">
                  <span className="text-zinc-500">{t('mfg.wo.actualHours')}: <b className="text-indigo-600 tabular-nums" data-testid="wo-actual-hours">{wo.actual_run_hours != null ? `${wo.actual_run_hours} ${t('mfg.bom.hoursUnit')}` : '—'}</b></span>
                  <span className="text-zinc-500">{t('mfg.bom.standardHours')}: <b className="tabular-nums">{wo.standard_hours != null ? `${wo.standard_hours} ${t('mfg.bom.hoursUnit')}` : '—'}</b></span>
                  <span className="text-zinc-500">{t('mfg.wo.laborEfficiency')}: <b className={`tabular-nums ${wo.labor_efficiency_pct == null ? 'text-zinc-400' : wo.labor_efficiency_pct >= 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="wo-labor-efficiency">{wo.labor_efficiency_pct != null ? `${wo.labor_efficiency_pct}%` : '—'}</b></span>
                </div>
                <div className="flex flex-wrap items-center gap-5 px-3 pb-3 text-sm border-t border-zinc-200 pt-3">
                  <span className="text-zinc-500">{t('mfg.wo.actualCost')}: <b className="text-emerald-600 tabular-nums" data-testid="wo-actual-cost">{wo.actual_labor_cost != null ? `¥${Number(wo.actual_labor_cost).toLocaleString()}` : '—'}</b></span>
                  <span className="text-zinc-500">{t('mfg.wo.standardCost')}: <b className="tabular-nums" data-testid="wo-standard-cost">{wo.standard_labor_cost != null ? `¥${Number(wo.standard_labor_cost).toLocaleString()}` : '—'}</b></span>
                  <span className="text-zinc-500">{t('mfg.wo.costVariance')}: <b className={`tabular-nums ${wo.labor_cost_variance == null ? 'text-zinc-400' : wo.labor_cost_variance <= 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="wo-cost-variance">{wo.labor_cost_variance != null ? `${wo.labor_cost_variance > 0 ? '+' : ''}¥${Number(wo.labor_cost_variance).toLocaleString()}` : '—'}</b></span>
                </div>
              </section>
            )}
            <section className="rounded-lg border border-zinc-200 overflow-hidden">
              <div className="px-3 py-2 border-b border-zinc-200"><h3 className="text-sm font-semibold text-zinc-700">{t('mfg.wo.components')}</h3></div>
              {comps.length === 0 ? <p className="px-3 py-6 text-center text-xs text-zinc-400">{t('mfg.wo.noComponents')}</p> : (
                <table className="w-full text-xs" data-testid="wo-components-table">
                  <thead><tr className="bg-zinc-50 text-zinc-400 border-b border-zinc-200">
                    <th className="text-left px-3 py-1.5">{t('mfg.bom.component')}</th>
                    <th className="text-right px-3 py-1.5">{t('mfg.wo.required')}</th>
                    <th className="text-right px-3 py-1.5">{t('mfg.wo.onHand')}</th>
                    <th className="text-right px-3 py-1.5">{t('mfg.wo.shortage')}</th>
                    <th className="text-right px-3 py-1.5">{t('mfg.wo.issued')}</th>
                  </tr></thead>
                  <tbody>{comps.map((c, i) => (
                    <tr key={c.component_item_id || i} className="border-b border-zinc-50" data-testid={`wo-comp-${i}`}>
                      <td className="px-3 py-1.5 text-zinc-700">
                        {c.name}{c.sku && <span className="ml-1.5 font-mono text-[10px] text-zinc-400">{c.sku}</span>}
                        {c.type && <Badge variant="outline" className={`ml-1.5 ${TYPE_BADGE[c.type] || ''}`}>{c.type_label || c.type}</Badge>}
                        {c.is_subcontracted && <span className="ml-1 inline-flex items-center text-orange-600" title={t('mfg.processing.subcontracted')}><Wrench size={12} weight="fill" /></span>}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{c.required_qty} {c.uom || ''}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{c.on_hand}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{(c.shortage || 0) > 0 ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">{c.shortage}</Badge> : <span className="text-emerald-600">0</span>}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500">{c.issued_qty || 0}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </section>

            {/* shortage requests */}
            {reqs.length > 0 && (
              <section className="rounded-lg border border-rose-100 overflow-hidden">
                <div className="px-3 py-2 border-b border-rose-100 bg-rose-50/50 flex items-center gap-1.5"><Package size={15} className="text-rose-500" /><h3 className="text-sm font-semibold text-rose-700">{t('mfg.wo.shortageReqs')}</h3></div>
                <ul className="divide-y divide-zinc-200" data-testid="wo-shortage-reqs">
                  {reqs.map((r) => (
                    <li key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <span className="font-mono text-xs text-zinc-500">{r.request_no}</span>
                      <span className="text-zinc-800 truncate flex-1">{r.name}</span>
                      <span className="tabular-nums text-zinc-600">{r.qty} {r.uom || ''}</span>
                      <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-200">{r.status}</Badge>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* product drawings (shared across every WO using this item) */}
            {wo.item_id && (
              <AttachmentsPanel
                testid="wo-product-attachments"
                entityType="item"
                entityId={wo.item_id}
                canWrite={canWrite}
                title={t('mfg.attach.productTitle')}
                hint={t('mfg.attach.productHint')}
              />
            )}

            {/* this work order's own attachments */}
            <AttachmentsPanel
              testid="wo-attachments"
              entityType="work_order"
              entityId={wo.id}
              canWrite={canWrite}
              title={t('mfg.attach.woTitle')}
            />

            {/* 2026-11-05 · 現場の記録と品質チェック（読み取り専用） */}
            {(qc?.work_runs || []).filter((r) => !r.undone_at).length > 0 && (
              <div className="pt-2 border-t border-zinc-200" data-testid="wo-detail-runs">
                <h3 className="font-semibold text-zinc-800">現場の記録</h3>
                <ul className="mt-1 space-y-1 text-sm text-zinc-700">
                  {qc.work_runs.filter((r) => !r.undone_at).map((r) => (
                    <li key={r.id} data-testid={`wo-detail-run-${r.no}`}>
                      記録{r.no}: OK {r.ok_qty} / NG {r.ng_qty}
                      {r.ng_reason ? `（${r.ng_reason}）` : ''} ・ 作業者 {r.operator_name}
                      {r.rev ? ` ・ rev ${r.rev}` : ''} ・ {(r.at || '').slice(5, 16).replace('T', ' ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(qc?.records || []).length > 0 && (
              <div className="pt-2 border-t border-zinc-200" data-testid="wo-detail-qc">
                <h3 className="font-semibold text-zinc-800">品質チェック</h3>
                <ul className="mt-1 space-y-1 text-sm text-zinc-700">
                  {qc.records.map((r) => (
                    <li key={r.id} data-testid={`wo-detail-qc-${r.id}`}>
                      {(r.at || '').slice(5, 16).replace('T', ' ')} ・ rev {r.rev} ・ {r.operator_name}
                      {(r.results || []).map((x) => (
                        <span key={x.check_id} className="ml-2">
                          {x.name}: {x.value ?? (x.document_id ? '写真' : '')}{x.uom || ''}
                          <span className={x.judgement === 'pass' ? 'text-emerald-700' : 'text-rose-700'}>
                            {x.judgement === 'pass' ? '合格' : x.judgement === 'pending' ? '未入力' : '基準外'}
                          </span>
                        </span>
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(tehaiIssues?.rows || []).length > 0 && (
              <div className="pt-2 border-t border-zinc-200" data-testid="wo-tehai-issues">
                <h3 className="font-semibold text-zinc-800">
                  この手配番号の出庫
                  <span className="ml-2 font-mono text-xs text-zinc-500">{tehaiIssues.tehai_no}</span>
                </h3>
                <ul className="mt-1 space-y-1 text-sm text-zinc-700">
                  {tehaiIssues.rows.map((r) => (
                    <li key={r.id} data-testid={`wo-tehai-issue-${r.id}`}>
                      {(r.at || '').slice(5, 16).replace('T', ' ')} ・ {r.item_name || '—'}
                      <span className="ml-2 tabular-nums">{r.quantity}{r.uom || ''}</span>
                      <span className="ml-2 text-zinc-500">¥{Number(r.cost || 0).toLocaleString()}</span>
                      {r.by_name ? <span className="ml-2 text-zinc-400">{r.by_name}</span> : null}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-sm font-semibold text-zinc-800" data-testid="wo-tehai-total">
                  合計 {tehaiIssues.total_qty} ・ 概算 ¥{Number(tehaiIssues.total_cost || 0).toLocaleString()}
                </p>
              </div>
            )}

            <LinkedDocsPanel targetType="mfg_work_order" targetId={wo.id}
              className="pt-2 border-t border-zinc-200" />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
